import { jsPlumb } from "jsplumb";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Divider, Image, Text } from "@chakra-ui/react";
import PDFTableFieldViewer from "../../features/extraction/components/PDFTableFieldViewer";
import PDFViewer from "../../features/extraction/components/PDFViewer";
import ReviewRowDetailPDF from "../../features/extraction/components/ReviewRowDetailPDF";
import { ExtractionDataItem } from "../../api/types";
import TopBar from "../../features/top-bar";
import { typedSxMap } from "../../utils/typedSxMap";
import { enumExtractionKey, extractions } from "./extractions";
import { images } from "./images";
import { useSessionContext } from "supertokens-auth-react/recipe/session";

const demoOptions = [
  { value: enumExtractionKey.PURCHASE_ORDER, label: enumExtractionKey.PURCHASE_ORDER, docType: "PDF", slug: "purchase-order" },
  { value: enumExtractionKey.INVOICE, label: enumExtractionKey.INVOICE, docType: "PDF", slug: "invoice" },
  { value: enumExtractionKey.BILL_OF_LADING, label: enumExtractionKey.BILL_OF_LADING, docType: "PDF", slug: "bill-of-lading" },
  { value: enumExtractionKey.PICK_PACK_LIST, label: enumExtractionKey.PICK_PACK_LIST, docType: "PDF", slug: "pick-pack-list" },
];

const jsPlumbConfig = {
  paintStyle: { stroke: "var(--blue-light-600)", strokeWidth: 2 },
  anchors: ["Right", "Left"],
  endpoints: [
    ["Blank", {}],
    ["Dot", { cssClass: "pdfDataEndpoint" }],
  ],
};

const connectorConfig = {
  stub: 20,
  gap: [0, 100],
  cornerRadius: 0,
  alwaysRespectStubs: true,
};

const transitionStyles = {
  fadeIn: {
    opacity: 1,
    transition: "opacity 0.1s ease-in-out",
  },
  fadeOut: {
    opacity: 0,
    transition: "opacity 0.1s ease-in-out",
  },
};

const styles = typedSxMap({
  mainContainer: {
    overflow: "hidden",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  content: {
    height: "calc(100vh - 64px)",
    background: "var(--color-background-main)",
    width: "100%",
    display: "flex",
  },
  documentWrapper: {
    flex: 1,
    padding: "var(--m-mm)",
  },
  container: {
    position: "relative",
    display: "flex",
    flex: 1,
    minWidth: "calc(100vw - 400px)",
    "& iframe": {
      flex: 3,
      width: "100%",
      height: "100%",
    },
    "& .reviewRowDetail .chakra-card": {
      height: "100%",
      maxHeight: "calc(100vh - 94px)",
    },
  },
  pdfViewerContainer: {
    paddingLeft: "11px",
  },
  reviewRowContainer: {
    flex: "1 0 50%",
    maxWidth: "50%",
  },
  demoSelectorContainer: {
    flexDirection: "column",
    display: "flex",
    width: "200px",
    minWidth: "200px",
    borderRight: "1px solid var(--color-border)",
    padding: "var(--m)",
    height: "100%",
    position: "sticky",
    top: 0,
    left: 0,
    background: "var(--color-background-main)",
    zIndex: 1,
    overflowY: "auto",
  },

  demoSelector: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    // marginTop: "var(--m)",
  },
  demoSelectorItem: {
    gap: "var(--m-xxs)",
    maxWidth: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: "var(--m-xxs)",
    paddingTop: "var(--m-xs)",
    borderRadius: "8px",
    border: "1px solid var(--color-border)",
    backgroundColor: "var(--color-background-demo-card)",
    "&:hover": {
      backgroundColor: "var(--color-background-demo-card-hover)",
      cursor: "pointer",
    },
    width: "100%",
  },
  selectedDemoSelectorItem: {
    backgroundColor: "var(--color-background-demo-card-hover)",
  },

  demoSelectorPreview: {
    borderRadius: "4px",
    width: "100px",
    height: "100%",
    backgroundColor: "var(--color-background-demo-card-placeholder)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    position: "relative",
  },

  docTypeBadge: {
    position: "absolute",
    top: "4px",
    right: "4px",
    background: "rgba(0, 0, 0, 0.5)",
    color: "rgba(255, 255, 255, 0.8)",
    fontSize: "xs",
    padding: "2px 6px",
    borderRadius: "4px",
  },

  demoSelectorPreviewImage: {
    width: "100%",
    transition: "opacity 0.3s ease 0s",
  },

  demoSelectorLabel: {
    fontWeight: "bold",
    color: "var(--color-text-strong)",
  },

  demoSelectorText: {
    display: "flex",
    flexDirection: "column",

    "&>p": {
      color: "var(--color-text-soft)",
    },
  },

  demoSelectorIcon: {
    width: "20px",
    margin: "auto",
  },
  divider: {
    // margin: "0 var(--m-xs) var(--m) var(--m-xs)",
    borderColor: "var(--color-border)",
    width: "unset",
  },
  connectionLayer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
    zIndex: 1,
  },
  clipMask: {
    position: "absolute",
    top: "-2px",
    left: "200px",
    right: 0,
    height: "100px",
    background: "var(--color-background-main)",
    zIndex: 2,
  },
});
export default function PDFDemo() {
  const location = useLocation();
  let navigate = useNavigate();
  const { pathname } = location;
  const [isTransitioning, setIsTransitioning] = useState(false);
  const sessionContext = useSessionContext() as any;
  const { doesSessionExist } = sessionContext;

  const jsPlumbInstanceRef = useRef<any>(null);
  const [selectedDataItem, setSelectedDataItem] = useState<ExtractionDataItem | null>();
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>();
  const [selectedDemoOption, setSelectedDemoOption] = useState<enumExtractionKey>(enumExtractionKey.PURCHASE_ORDER);

  const selectedExtraction = extractions[selectedDemoOption];

  const refreshConnection = useCallback(() => {
    if (!jsPlumbInstanceRef.current || !selectedDataItem || !selectedTemplateKey) {
      return;
    }
    const sourceId = `item-${selectedDataItem.page_element?.id}`;
    const targetId = `details-${selectedDataItem.page_element?.id}-${selectedTemplateKey}`;

    jsPlumbInstanceRef.current.deleteEveryConnection();
    jsPlumbInstanceRef.current.reset();

    jsPlumbInstanceRef.current.connect({
      source: sourceId,
      target: targetId,
      connector: ["Straight", connectorConfig],
      ...jsPlumbConfig,
    });
  }, [selectedDataItem, selectedTemplateKey]);

  useEffect(() => {
    refreshConnection();
  }, [selectedDataItem, refreshConnection]);

  useEffect(() => {
    document.title = "TableFlow Extraction Demo";
    return () => {
      document.title = "TableFlow";
    };
  }, []);

  useEffect(() => {
    if (jsPlumbInstanceRef.current) {
      jsPlumbInstanceRef.current.deleteEveryConnection();
      jsPlumbInstanceRef.current.reset();
    }
    setTimeout(() => {
      // Have the first field automatically selected (but not the table field if there is a table) so there is a jsplumb line drawn to it
      const initialSelectedTemplateKey = selectedExtraction.template.filter((t) => t.data_type !== "table")[0]?.key;
      const initialSelectedDataItem = selectedExtraction.data[initialSelectedTemplateKey];
      setSelectedDataItem(initialSelectedDataItem);
      setSelectedTemplateKey(initialSelectedTemplateKey);
    }, 100);
  }, [selectedDemoOption]);

  const handleScroll = useCallback(() => {
    if (jsPlumbInstanceRef.current) {
      jsPlumbInstanceRef.current.repaintEverything();
    }
  }, []);

  useEffect(() => {
    const demoOptionFromUrl = decodeURIComponent(pathname.substring(pathname.lastIndexOf("/") + 1));
    const demoOption = demoOptions.find((option) => option.slug === demoOptionFromUrl);
    if (demoOption) {
      setIsTransitioning(true);
      setTimeout(() => {
        setSelectedDemoOption(demoOption.value as enumExtractionKey);
        setSelectedDataItem(null);
        setSelectedTemplateKey(null);
        setIsTransitioning(false);
      }, 100);
    }
  }, [pathname]);

  useEffect(() => {
    const instance = jsPlumb.getInstance({
      Container: "connection-layer",
      Connector: ["Straight", connectorConfig],
      ...jsPlumbConfig,
    });
    jsPlumbInstanceRef.current = instance;

    const handleResize = () => {
      if (jsPlumbInstanceRef.current) {
        jsPlumbInstanceRef.current.repaintEverything();
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      instance.deleteEveryConnection();
      instance.deleteEveryEndpoint();
      instance.reset();
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleUpdateDataItem = useCallback((updatedItem: ExtractionDataItem, key: string) => {}, []);

  return (
    <Box sx={styles.mainContainer}>
      <TopBar logoLink={doesSessionExist ? "/" : "https://tableflow.com"} showBookADemoButton={true} />
      <Box sx={styles.clipMask} />
      <Box sx={styles.content}>
        <Box sx={styles.connectionLayer} id="connection-layer" />
        <Box sx={styles.demoSelectorContainer}>
          {/* <Text as="h3" fontSize="md" fontWeight={700}>
            Choose Document
          </Text> */}
          <Box sx={styles.demoSelector}>
            {demoOptions.map((option) => (
              <Box
                sx={{
                  ...styles.demoSelectorItem,
                  ...(selectedDemoOption === option.value ? styles.selectedDemoSelectorItem : {}),
                }}
                key={option.value}
                onClick={() => {
                  navigate(`/extraction-demo/${option.slug}`);
                }}>
                <Box sx={styles.demoSelectorPreview}>
                  <Image sx={styles.demoSelectorPreviewImage} src={images[option.value].url}></Image>
                  <Text sx={styles.docTypeBadge}>{option.docType}</Text>
                </Box>
                <Box sx={styles.demoSelectorText}>
                  <Text as="b" sx={styles.demoSelectorLabel}>
                    {option.label}
                  </Text>
                </Box>
              </Box>
            ))}
            {/* TODO: Uncomment when upload is implemented */}
            {/* <Box sx={styles.demoSelectorItem} key={"upload"}>
            <Box sx={styles.demoSelectorPreview}>
              <FiUpload size={30} />
            </Box>
            <Box sx={styles.demoSelectorText}>
              <Text as="b" sx={styles.demoSelectorLabel}>
                Upload File
              </Text>
              <Text as="p">PDF or Image</Text>
            </Box>
          </Box> */}
          </Box>
        </Box>
        <Divider sx={styles.divider} />
        {selectedExtraction && (
          <Box sx={styles.documentWrapper}>
            <Box sx={styles.container} tabIndex={0} id="jsPlumbContainer">
              <Box sx={isTransitioning ? transitionStyles.fadeOut : transitionStyles.fadeIn}>
                {images[selectedDemoOption] && (
                  <Box sx={styles.pdfViewerContainer}>
                    <PDFViewer
                      data={selectedExtraction.data}
                      selectedDataItem={selectedDataItem}
                      setSelectedDataItem={(item: ExtractionDataItem | null, key: string) => {
                        setSelectedTemplateKey(key);
                        setSelectedDataItem(item);
                      }}
                      pdfImageUrl={images[selectedDemoOption].url}
                      onScroll={handleScroll}
                      independentScroll={true}
                    />
                  </Box>
                )}
                {selectedTemplateKey && selectedDataItem && (
                  <PDFTableFieldViewer
                    selectedDataItem={selectedDataItem || ({} as any)}
                    template={selectedExtraction?.template || []}
                    selectedTemplateKey={selectedTemplateKey || ""}
                    extractionId={selectedExtraction?.id}
                    onUpdateDataItem={() => {}}
                    isEditable={selectedExtraction?.status === "review"}
                    isDemo={true}
                  />
                )}
              </Box>
              <Box sx={styles.reviewRowContainer}>
                <ReviewRowDetailPDF
                  setSelectedDataItem={setSelectedDataItem}
                  selectedDataItem={selectedDataItem}
                  data={selectedExtraction.data}
                  extractionId={selectedExtraction?.id}
                  isReadOnly={true}
                  template={selectedExtraction?.template || []}
                  onScroll={handleScroll}
                  selectedTemplateKey={selectedTemplateKey}
                  setSelectedTemplateKey={setSelectedTemplateKey}
                  onUpdateDataItem={handleUpdateDataItem}
                  leftAdjustment={"25px"}
                  topAdjustment={"0px"}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
